import ListItemText from "@mui/material/ListItemText";
import { grey } from "designSystem/styles/color";
import { ColorSpec } from "designSystem/components/colorchip/ColorChipArrayStore";
import { ButtonEnum } from "designSystem/types/button-types";
import { useHover } from "designSystem/hooks";
import {
	Box,
	Button,
	ColorChipArray, CsiImage,
	DataGridCardProps,
	FlexColumn,
	Link,
	ListItem,
	ListWithSubheader,
	MoreHorizMenu,
	OverflowText,
	PopupDialog,
	Text
} from "designSystem/index";
import { ListSection } from "designSystem/components/list/ListWithSubheader";
import { useLocale } from "designSystem/locale";
import DojoGalleryAttributes from "csi/page/DojoGalleryAttributes";
import DojoView from "csi/page/DojoView";
import session from "csi/session";
import { C8Node } from "csi/typings";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent, useRef, useState } from "react";
import { getProductAttributeSpecs } from "../PPService";
import PPInvalidTag from "./PPInvalidTag";
import { OPTIONS, classes, StyledFlexColumn } from "./PPProductCard";
import { usePPWizardStore } from "./PPWizardStore";
import { useProductGalleryStore } from "./ProductGalleryStore";

const SubList = observer(() => {
	const { enableViewInfoInViolationPredictionDialog } = usePPWizardStore();
	const loc = useLocale();

	const optionsSection: ListSection = {
		sectionName: loc.resource(OPTIONS),
		sectionBody: (
			<ListItem design="list" onClick={enableViewInfoInViolationPredictionDialog}>
				<ListItemText primary={loc.resource("ViewInfo")} />
			</ListItem>
		),
	};

	return <ListWithSubheader sections={[optionsSection]} />;
});

export interface PredictViolationProductCardProps extends DataGridCardProps<C8Node> { }

export const PredictViolationProductCard: FunctionComponent<PredictViolationProductCardProps> = observer((props) => {
	const { item } = props;

	const { futureViolationUrlsOfProduct, futureViolationColorUrls, disableViewInfoInViolationPredictionDialog, viewInfoInViolationPredictionDialog } = usePPWizardStore();
	const loc = useLocale();

	const productHasViolation = futureViolationUrlsOfProduct.has(item.$URL);
	const hoverRef = useRef(null);
	const hovering = useHover(hoverRef);

	const {
		cache,
		getColorSpecsForViolationPrediction,
		getDefaultColorSpecForViolationPrediction,
		getImageNode,
		onOpenGalleryCardMenu,
		showAttributes,
		supportsColor,
	} = useProductGalleryStore();

	const attributeSpecs = getProductAttributeSpecs(item);
	const colors = getColorSpecsForViolationPrediction(item, productHasViolation, futureViolationColorUrls);
	const defaultColor = getDefaultColorSpecForViolationPrediction(item, productHasViolation, colors);
	const [color, setColor] = useState<ColorSpec>(defaultColor);
	const hasColor = supportsColor(item);
	const imageNode = getImageNode(color || defaultColor || colors[0]);
	const typeName = loc.type(item.$Type);
	const locationUrl = session.getLocationForUrl(item.$URL);
	const dialogTitle = loc.getNodeName(item);

	return (
		<StyledFlexColumn className={classes.root} {... { ref: hoverRef }}>
			<CsiImage
				allowSwitchNodeImages
				aspectRatio={1}
				displayControls={hovering}
				noImageComponent={(
					<Text
						lineHeight={16}
						res="NoImageAvailable"
						semiBold
						size={14}
						style={{ color: grey[800] }}
					/>
				)}
				node={imageNode}
			/>
			<Box p={3}>
				<OverflowText
					bold
					lineHeight={16}
					size={14}
					text={item.$Name}
				/>
			</Box>
			<Box flexGrow={1}>
				{hasColor && (
					<Box pb={3} px={3}>
						<ColorChipArray
							colors={colors}
							defaultColor={defaultColor}
							onSelectChip={setColor}
							value={color}
						/>
					</Box>
				)}
				{attributeSpecs && showAttributes && (
					<Box pb={3} px={3}>
						<DojoGalleryAttributes
							cache={cache}
							node={item}
							showLabels
							specs={attributeSpecs}
						/>
					</Box>
				)}
			</Box>
			<MoreHorizMenu
				className={classes.menu}
				design="outline"
				hovering={hovering}
				onOpen={onOpenGalleryCardMenu.bind(this, item)}
				round
				shadow
				showOnHover
			>
				<SubList />
			</MoreHorizMenu>
			<Box className={classes.invalidation}>
				<PPInvalidTag res="PresentationItemInvalidTag" />
			</Box>
			{viewInfoInViolationPredictionDialog && (
				<PopupDialog
					disableEnforceFocus
					fullHeight
					minWidth={1290}
					onClose={disableViewInfoInViolationPredictionDialog}
					open
					title={dialogTitle}
				>
					{{
						main: (
							<DojoView
								forceReadOnly
								nodeUrl={item.$URL}
								viewId="Properties"
								viewParams={{ bPopup: true }}
							/>
						),
						buttons: (
							<Fragment>
								<Link
									href={locationUrl}
									target="_blank"
								>
									<Text
										res="GoTo"
										resParams={[typeName]}
									/>
								</Link>
								<Button
									design="secondary"
									onClick={disableViewInfoInViolationPredictionDialog}
									res={ButtonEnum.BUTTON_CLOSE}
								/>
							</Fragment>
						),
					}}
				</PopupDialog>
			)}
		</StyledFlexColumn>
	);
});

export default PredictViolationProductCard;